/**
 * BXYZ:..:eliot@bosmanxyz.xyz:..:.www.bosmanxyz.xyz
 * bxyz-header - header funktionalitet med gsap animationer
 */

/* ============================================
   header state
   ============================================ */

const bxyzHeader = {
  header: null,
  mobileMenu: null,
  menuTrigger: null,
  lastScrollY: 0,
  scrollThreshold: 100,
  isMenuOpen: false,
  isHidden: false,

  /* ============================================
     initiering
     ============================================ */

  init() {
    this.header = document.querySelector('[data-header]');
    if (!this.header) return;

    this.mobileMenu = document.querySelector('[data-mobile-menu]');
    this.menuTrigger = document.querySelector('[data-menu-trigger]');

    this.bindEvents();
    this.initDropdowns();
    this.initMobileAccordions();
    this.initScrollBehavior();
  },

  /* ============================================
     händelser
     ============================================ */

  bindEvents() {
    // mobil meny toggle
    if (this.menuTrigger) {
      this.menuTrigger.addEventListener('click', () => {
        this.toggleMobileMenu();
      });
    }

    // stäng meny vid klick på länk
    if (this.mobileMenu) {
      this.mobileMenu.querySelectorAll('a').forEach((link) => {
        link.addEventListener('click', () => {
          this.closeMobileMenu();
        });
      });
    }

    // stäng med escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isMenuOpen) {
        this.closeMobileMenu();
      }
    });

    // sök trigger
    document.querySelectorAll('[data-search-trigger]').forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.openSearch();
      });
    });
  },

  /* ============================================
     mobil meny
     ============================================ */

  toggleMobileMenu() {
    if (this.isMenuOpen) {
      this.closeMobileMenu();
    } else {
      this.openMobileMenu();
    }
  },

  openMobileMenu() {
    if (this.isMenuOpen) return;
    this.isMenuOpen = true;

    document.documentElement.classList.add('scroll-lock');
    this.mobileMenu.classList.add('is-open');
    this.mobileMenu.setAttribute('aria-hidden', 'false');
    this.menuTrigger.setAttribute('aria-expanded', 'true');

    // gsap animation
    if (typeof gsap !== 'undefined') {
      gsap.fromTo(
        this.mobileMenu,
        { opacity: 0 },
        { opacity: 1, duration: 0.3, ease: 'power2.out' }
      );

      const menuItems = this.mobileMenu.querySelectorAll('.bxyz-mobile-menu__item');
      gsap.fromTo(
        menuItems,
        { opacity: 0, x: -20 },
        {
          opacity: 1,
          x: 0,
          duration: 0.4,
          stagger: 0.05,
          ease: 'power2.out',
          delay: 0.1,
        }
      );
    }
  },

  closeMobileMenu() {
    if (!this.isMenuOpen) return;

    // gsap animation
    if (typeof gsap !== 'undefined') {
      gsap.to(this.mobileMenu, {
        opacity: 0,
        duration: 0.3,
        ease: 'power2.out',
        onComplete: () => {
          this.isMenuOpen = false;
          document.documentElement.classList.remove('scroll-lock');
          this.mobileMenu.classList.remove('is-open');
          this.mobileMenu.setAttribute('aria-hidden', 'true');
          this.menuTrigger.setAttribute('aria-expanded', 'false');
        },
      });
    } else {
      this.isMenuOpen = false;
      document.documentElement.classList.remove('scroll-lock');
      this.mobileMenu.classList.remove('is-open');
      this.mobileMenu.setAttribute('aria-hidden', 'true');
      this.menuTrigger.setAttribute('aria-expanded', 'false');
    }
  },

  /* ============================================
     dropdowns
     ============================================ */

  initDropdowns() {
    const dropdownTriggers = document.querySelectorAll('[data-dropdown-trigger]');

    dropdownTriggers.forEach((trigger) => {
      const dropdown = trigger.nextElementSibling;
      if (!dropdown) return;

      // hover för desktop
      const parent = trigger.parentElement;

      parent.addEventListener('mouseenter', () => {
        this.openDropdown(trigger, dropdown);
      });

      parent.addEventListener('mouseleave', () => {
        this.closeDropdown(trigger, dropdown);
      });

      // klick för tillgänglighet
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        const isOpen = trigger.getAttribute('aria-expanded') === 'true';
        if (isOpen) {
          this.closeDropdown(trigger, dropdown);
        } else {
          this.openDropdown(trigger, dropdown);
        }
      });

      // tangentbord
      trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const isOpen = trigger.getAttribute('aria-expanded') === 'true';
          if (isOpen) {
            this.closeDropdown(trigger, dropdown);
          } else {
            this.openDropdown(trigger, dropdown);
          }
        }
      });
    });
  },

  openDropdown(trigger, dropdown) {
    trigger.setAttribute('aria-expanded', 'true');

    if (typeof gsap !== 'undefined') {
      gsap.fromTo(
        dropdown,
        { opacity: 0, y: -10 },
        { opacity: 1, y: 0, duration: 0.2, ease: 'power2.out' }
      );
    }
  },

  closeDropdown(trigger, dropdown) {
    trigger.setAttribute('aria-expanded', 'false');

    if (typeof gsap !== 'undefined') {
      gsap.to(dropdown, {
        opacity: 0,
        y: -10,
        duration: 0.2,
        ease: 'power2.out',
      });
    }
  },

  /* ============================================
     mobil accordions
     ============================================ */

  initMobileAccordions() {
    const accordionTriggers = document.querySelectorAll('[data-accordion-trigger]');

    accordionTriggers.forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const isOpen = trigger.getAttribute('aria-expanded') === 'true';
        const content = trigger.nextElementSibling;

        if (isOpen) {
          trigger.setAttribute('aria-expanded', 'false');
          if (typeof gsap !== 'undefined') {
            gsap.to(content, {
              height: 0,
              opacity: 0,
              duration: 0.3,
              ease: 'power2.out',
            });
          } else {
            content.style.display = 'none';
          }
        } else {
          trigger.setAttribute('aria-expanded', 'true');
          if (typeof gsap !== 'undefined') {
            gsap.fromTo(
              content,
              { height: 0, opacity: 0 },
              { height: 'auto', opacity: 1, duration: 0.3, ease: 'power2.out' }
            );
          } else {
            content.style.display = 'block';
          }
        }
      });
    });
  },

  /* ============================================
     scroll beteende
     ============================================ */

  initScrollBehavior() {
    const isSticky = this.header.dataset.sticky === 'true';
    if (!isSticky) return;

    let ticking = false;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // lägg till/ta bort scrolled klass
      if (currentScrollY > this.scrollThreshold) {
        this.header.classList.add('is-scrolled');
      } else {
        this.header.classList.remove('is-scrolled');
      }

      // göm/visa header vid scroll
      if (currentScrollY > this.lastScrollY && currentScrollY > this.scrollThreshold * 2) {
        // scrollar ner - göm header
        if (!this.isHidden && !this.isMenuOpen) {
          this.hideHeader();
        }
      } else {
        // scrollar upp - visa header
        if (this.isHidden) {
          this.showHeader();
        }
      }

      this.lastScrollY = currentScrollY;
      ticking = false;
    };

    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(handleScroll);
        ticking = true;
      }
    });
  },

  hideHeader() {
    this.isHidden = true;
    this.header.classList.add('is-hidden');

    if (typeof gsap !== 'undefined') {
      gsap.to(this.header, {
        y: '-100%',
        duration: 0.3,
        ease: 'power2.out',
      });
    }
  },

  showHeader() {
    this.isHidden = false;
    this.header.classList.remove('is-hidden');

    if (typeof gsap !== 'undefined') {
      gsap.to(this.header, {
        y: '0%',
        duration: 0.3,
        ease: 'power2.out',
      });
    }
  },

  /* ============================================
     sök
     ============================================ */

  openSearch() {
    // trigger shopify's search modal om det finns
    const searchModal = document.querySelector('[data-search-modal]');
    if (searchModal) {
      searchModal.classList.add('is-open');
      document.documentElement.classList.add('scroll-lock');
    }

    // eller öppna predictive search
    const predictiveSearch = document.querySelector('predictive-search');
    if (predictiveSearch) {
      const input = predictiveSearch.querySelector('input[type="search"]');
      if (input) {
        input.focus();
      }
    }
  },
};

/* ============================================
   initiering
   ============================================ */

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => bxyzHeader.init());
} else {
  bxyzHeader.init();
}

// exportera för extern användning
window.bxyzHeader = bxyzHeader;
